import {Box, VStack} from 'platform/foundation-tw';

import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Navigate, useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {Banner} from '../components/Banner/Banner';
import {PredictionReport} from '../components/PredictionReport/PredictionReport';
import {useVinPredictionFlow} from '../hooks/useVinPredictionFlow';

interface ReportContentProps {
  vin: string;
}

function ReportContent(props: ReportContentProps) {
  const {t} = useTranslation();
  const flow = useVinPredictionFlow({vin: props.vin});
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);

  if (flow.notFound) {
    return (
      <Box maxWidth="108rem" center minHeight="100dvh">
        <Box paddingHorizontal={8} paddingTop={8}>
          <VStack spacing={4}>
            <Banner
              tone="warning"
              title={t('report.notEnriched.title')}
              description={
                <>
                  {t('report.notEnriched.description')} (VIN:{' '}
                  <span className="font-mono">{props.vin}</span>)
                </>
              }
              testId="vinNotEnrichedBanner"
            />
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <PredictionReport
      vin={props.vin}
      predictionData={flow.predictionData}
      rulData={flow.rulData}
      isLoading={flow.isLoading}
      error={flow.error}
      selectedPartId={selectedPartId}
      onPartClick={setSelectedPartId}
    />
  );
}

export function Report() {
  const params = useParams<{vin: string}>();

  if (isNil(params.vin)) {
    return <Navigate to="/" replace />;
  }

  // Key by VIN so the prediction flow re-runs cleanly when the VIN changes via
  // route navigation. `useVinPredictionFlow` uses `useOnMount` (per repo rule
  // banning direct `useEffect`); the `key` prop reset pattern is the approved
  // way to "rerun on input change" in that case.
  return <ReportContent key={params.vin} vin={params.vin} />;
}
